import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionPlans } from '../hooks/useSubscriptionPlans';
import { getApiBaseUrl } from '../utils/apiConfig';
import './Homepage.css';

const Homepage = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('annual'); // Default to annual
  const [emails, setEmails] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailPopup, setShowEmailPopup] = useState(null);
  const [billingConfig, setBillingConfig] = useState({
    freeModeEnabled: true,
    freeModePlanId: 'free',
    freeModeTrialDays: 60,
    freeModeMessage: ''
  });

  // Fetch dynamic subscription plans
  const { plans: dynamicPlans, loading: plansLoading } = useSubscriptionPlans();

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const loadBillingConfig = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/onboarding/environment-status`);
        if (!response.ok) {
          throw new Error('Failed to load environment status');
        }
        const result = await response.json();
        const freeMode = result?.free_mode || {};
        setBillingConfig({
          freeModeEnabled: Boolean(freeMode.enabled),
          freeModePlanId: freeMode.plan_id || 'free',
          freeModeTrialDays: typeof freeMode.trial_days === 'number' ? freeMode.trial_days : 60,
          freeModeMessage: freeMode.message || ''
        });
      } catch (err) {
        console.error('Unable to load billing configuration', err);
        // Default to complimentary access messaging when backend is unreachable
        setBillingConfig((prev) => ({
          ...prev,
          freeModeEnabled: true
        }));
      }
    };

    loadBillingConfig();
  }, []);

  // Handle start trial from pricing section
  const handleStartTrial = async (planId) => {
    setSelectedPlan(planId);
    setError('');
    setShowEmailPopup(null);

    const email = emails[planId] || '';

    if (!email || !email.includes('@')) {
      setShowEmailPopup(planId);
      setTimeout(() => setShowEmailPopup(null), 4000);
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = `${getApiBaseUrl()}/onboarding/signup-lead`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim(),
          clinic_name: '',
          password: '',
          contact_phone: '',
          timezone: 'America/New_York',
          currency: 'GBP',
          selected_plan: planId,
          billing_period: billingPeriod
        })
      });

      const result = await response.json();

      if (result.success) {
        navigate(`/check-inbox?lead_id=${result.lead_id}&email=${encodeURIComponent(email)}&plan=${planId}`);
      } else {
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isFreeMode = billingConfig.freeModeEnabled;
  const trialDays = billingConfig.freeModeTrialDays || 60;
  const freeModeMessage =
    billingConfig.freeModeMessage ||
    'We are waiving billing during launch—activate your workspace and explore everything MedInsight offers.';

  const freePlanFeatures = [
    'Full CRM marketing automation during the complimentary period',
    'Instant tenant provisioning and Pabau integration hooks',
    'Campaign templates, analytics, and automation workflows included',
    'Upgrade to a paid plan whenever billing resumes—no downtime'
  ];

  const pricingPlans = isFreeMode
    ? {
      [billingConfig.freeModePlanId || 'free']: {
        name: 'MedInsight Free Plan',
        monthlyPrice: 0,
        annualPrice: 0,
        description: trialDays
          ? `Enjoy ${trialDays}-day complimentary access with full MedInsight capabilities.`
          : 'Enjoy complimentary access with full MedInsight capabilities.',
        features: freePlanFeatures,
        highlighted: true,
        priceSuffix: '/launch promo',
        ctaText: 'Activate Free Plan'
      }
    }
    : {
      basic: {
        name: 'Basic Protection',
        monthlyPrice: 99,
        annualPrice: 996,
        description: 'Small clinics up to 1,000 patients',
        features: [
          'Real-time data backup',
          'Appointment access',
          'Patient records',
          'Email support',
          '14-day free trial'
        ],
        ctaText: 'Start Free Trial'
      },
      pro: {
        name: 'Professional Protection',
        monthlyPrice: 200,
        annualPrice: 2004,
        description: 'Growing practices up to 10,000 patients',
        features: [
          'Everything in Basic',
          'Priority support',
          'Advanced reporting',
          'Multi-location support',
          'Faster sync intervals',
          '14-day free trial'
        ],
        highlighted: true,
        ctaText: 'Start Free Trial'
      },
      enterprise: {
        name: 'Enterprise Protection',
        monthlyPrice: 350,
        annualPrice: 3504,
        description: 'Large clinics with unlimited patients',
        features: [
          'Everything in Professional',
          'Dedicated account manager',
          'Custom onboarding',
          'Phone support',
          'SLA guarantees',
          '14-day free trial'
        ],
        ctaText: 'Start Free Trial'
      }
    };

  const getDisplayPrice = (plan) => {
    if (billingPeriod === 'annual') {
      return Math.round(plan.annualPrice / 12);
    }
    return plan.monthlyPrice;
  };

  const getSavings = (plan) => {
    const monthlyCost = plan.monthlyPrice * 12;
    const annualCost = plan.annualPrice;
    return Math.round(((monthlyCost - annualCost) / monthlyCost) * 100);
  };

  return (
    <div className="homepage">
      {/* Navigation Header */}
      <nav className="homepage-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <h2>MedInsight Pro</h2>
          </div>
          <div className="nav-links">
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}>
              How It Works
            </a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>
              Pricing
            </a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>
              FAQ
            </a>
            <button className="nav-signin" onClick={() => navigate('/login')}>
              Sign In
            </button>
            <button className="nav-cta" onClick={() => scrollToSection('pricing')}>
              Start Free Trial →
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1>Sleep soundly knowing your clinic never stops</h1>
          <p className="hero-subheader">
            Complete Pabau backup that keeps your practice running, even when Pabau doesn't
          </p>
          <p className="hero-supporting">
            What if Pabau went down right now? Your appointments, patient records, and revenue stream
            protected with MedInsight Pro's continuous backup system.
          </p>
          <button className="hero-cta" onClick={() => scrollToSection('pricing')}>
            Protect Your Practice Today
          </button>
          <p className="hero-cta-subtext">
            14-day free trial • No risk • Setup in minutes
          </p>
        </div>
      </section>

      {/* Problem Agitation Section */}
      <section className="problem-section">
        <div className="container">
          <h2>The call every clinic owner dreads</h2>
          <div className="story-box">
            <p>
              "It's 8:47 AM. Your first patient arrives in 13 minutes. Pabau won't load.
              Your staff is panicking. Your day is about to become a £2,000+ loss."
            </p>
          </div>
          <div className="pain-points-grid">
            <div className="pain-point">
              <span className="pain-icon">💰</span>
              <h3>Lost Revenue</h3>
              <p>Cancelled appointments mean thousands in lost income</p>
            </div>
            <div className="pain-point">
              <span className="pain-icon">⏰</span>
              <h3>Idle Staff</h3>
              <p>Your team costs money even when systems are down</p>
            </div>
            <div className="pain-point">
              <span className="pain-icon">😤</span>
              <h3>Reputation Risk</h3>
              <p>Angry patients share bad experiences quickly</p>
            </div>
            <div className="pain-point">
              <span className="pain-icon">🔒</span>
              <h3>Data Darkness</h3>
              <p>Can't access patient histories or treatment plans</p>
            </div>
            <div className="pain-point">
              <span className="pain-icon">📝</span>
              <h3>Manual Chaos</h3>
              <p>Paper workarounds waste hours of productivity</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="container">
          <h2>What if downtime didn't matter anymore?</h2>
          <div className="features-list">
            <div className="feature-item">
              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Patient records updated in real-time via webhooks</span>
            </div>
            <div className="feature-item">
              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Appointment schedules accessible offline</span>
            </div>
            <div className="feature-item">
              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Complete treatment histories at your fingertips</span>
            </div>
            <div className="feature-item">
              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Staff can continue working normally</span>
            </div>
            <div className="feature-item">
              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Patients never know there was a problem</span>
            </div>
          </div>
          <p className="solution-subheader">
            While other clinics scramble, you operate with confidence.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2>Protection that runs itself</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Connect</h3>
              <p>Link your Pabau account securely (one-time setup)</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Sync</h3>
              <p>Real-time webhooks keep your backup current automatically</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Relax</h3>
              <p>Access your data instantly whenever you need it</p>
            </div>
          </div>
          <p className="how-it-works-note">
            No IT expertise required • Works with your existing Pabau setup
          </p>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="trust-section">
        <div className="container">
          <h2>Enterprise-grade protection for your practice</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <span className="trust-icon">🔒</span>
              <h3>GDPR & HIPAA Compliant</h3>
              <p>Your patient data handled with maximum security</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🛡️</span>
              <h3>Bank-Level Encryption</h3>
              <p>256-bit SSL encryption for all data</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">⚡</span>
              <h3>Real-Time Sync</h3>
              <p>Webhooks ensure zero data lag</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🌍</span>
              <h3>Global Protection</h3>
              <p>Trusted by clinics across the world</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🔄</span>
              <h3>99.9% Uptime</h3>
              <p>Multiple redundancy systems ensure we're always available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-section">
        <div className="container">
          <h2>Less than one hour of clinical work per month</h2>
          <p className="value-description">
            For a high-performing aesthetics clinic, even our Enterprise protection level costs less than
            one hour of treatments. Your Basic protection starts at just £83/month - less than a single
            cancelled appointment.
          </p>
          <div className="cost-comparison-box">
            <div className="comparison-item">
              <span className="comparison-label">Average appointment value:</span>
              <span className="comparison-value">£150-300</span>
            </div>
            <div className="comparison-item">
              <span className="comparison-label">Cost of one day's downtime:</span>
              <span className="comparison-value">£2,000+</span>
            </div>
            <div className="comparison-item">
              <span className="comparison-label">Monthly protection cost:</span>
              <span className="comparison-value">From £83</span>
            </div>
            <div className="comparison-item highlighted">
              <span className="comparison-label">ROI:</span>
              <span className="comparison-value">One prevented incident pays for 2+ years of protection</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2>{isFreeMode ? 'Complimentary Access' : 'Choose your protection level'}</h2>
          <p className="pricing-subheader">
            {isFreeMode
              ? freeModeMessage
              : 'Most clinics choose annual protection for uninterrupted peace of mind'}
          </p>

          {/* Billing Toggle */}
          {!isFreeMode && (
            <div className="billing-toggle">
              <span className={billingPeriod === 'annual' ? 'active' : ''}>
                Smart Billing (Annual)
              </span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={billingPeriod === 'monthly'}
                  onChange={(e) => setBillingPeriod(e.target.checked ? 'monthly' : 'annual')}
                />
                <span className="slider"></span>
              </label>
              <span className={billingPeriod === 'monthly' ? 'active' : ''}>
                Flexible Billing (Monthly)
              </span>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="pricing-cards-grid">
            {Object.entries(pricingPlans).map(([planId, plan]) => (
              <div key={planId} className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}>
                {plan.highlighted && !isFreeMode && <div className="popular-badge">Most Popular</div>}

                <div className="card-header">
                  <h3>{plan.name}</h3>
                  <p>{plan.description}</p>
                </div>

                <div className="card-pricing">
                  {isFreeMode ? (
                    <div className="price">
                      <span className="currency">£</span>
                      <span className="amount">0</span>
                      <span className="period">{plan.priceSuffix || '/launch promo'}</span>
                    </div>
                  ) : (
                    <>
                      <div className="price">
                        <span className="currency">£</span>
                        <span className="amount">{getDisplayPrice(plan)}</span>
                        <span className="period">/month*</span>
                      </div>
                      {billingPeriod === 'annual' && (
                        <>
                          <div className="billed-annually">
                            *Billed annually (£{plan.annualPrice}/year)
                          </div>
                          <div className="annual-savings">
                            Save {getSavings(plan)}% with annual billing
                          </div>
                        </>
                      )}
                      {billingPeriod === 'monthly' && (
                        <div className="billed-monthly">
                          Billed monthly
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="card-features">
                  <ul>
                    {plan.features.map((feature, index) => (
                      <li key={index}>
                        <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-action">
                  <div className="email-capture">
                    <input
                      type="email"
                      placeholder="Enter your company email"
                      value={emails[planId] || ''}
                      onChange={(e) => {
                        setEmails(prev => ({
                          ...prev,
                          [planId]: e.target.value
                        }));
                        if (showEmailPopup) {
                          setShowEmailPopup(null);
                        }
                      }}
                      className="email-input"
                      disabled={isLoading}
                    />
                    <div className="button-container">
                      <button
                        className={`cta-button ${(plan.highlighted || isFreeMode) ? 'primary' : 'secondary'}`}
                        onClick={() => handleStartTrial(planId)}
                        disabled={isLoading}
                      >
                        {isLoading && selectedPlan === planId
                          ? (isFreeMode ? 'Activating...' : 'Starting...')
                          : plan.ctaText || (isFreeMode ? 'Activate Free Plan' : 'Start Free Trial')}
                      </button>

                      {/* Email Required Popup */}
                      {showEmailPopup === planId && (
                        <div className="email-popup">
                          <div className="popup-content">
                            <span className="popup-icon">📧</span>
                            <span className="popup-text">
                              {isFreeMode
                                ? 'Please enter your email address to activate your complimentary access'
                                : 'Please enter your email address to start your free trial'}
                            </span>
                          </div>
                          <div className="popup-arrow"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <h2>Common questions about MedInsight Pro</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly does the data sync?</h3>
              <p>Real-time. We use webhooks and APIs to capture every change in Pabau instantly, so your backup is always current.</p>
            </div>
            <div className="faq-item">
              <h3>Is my patient data secure?</h3>
              <p>Absolutely. We follow GDPR and HIPAA compliance standards with bank-level 256-bit SSL encryption for all data transmission and storage.</p>
            </div>
            <div className="faq-item">
              <h3>What if MedInsight Pro goes down?</h3>
              <p>We maintain 99.9% uptime with multiple redundancy systems. Your data is stored across multiple secure servers to ensure constant availability.</p>
            </div>
            <div className="faq-item">
              <h3>Will this interfere with my current Pabau setup?</h3>
              <p>Not at all. MedInsight Pro works alongside Pabau without any disruption. Your team won't even notice it's running.</p>
            </div>
            <div className="faq-item">
              <h3>How difficult is the setup?</h3>
              <p>Simple. Just connect your Pabau credentials, and we handle everything else. Most clinics are protected within 10 minutes.</p>
            </div>
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes, upgrade or downgrade anytime. Changes take effect on your next billing cycle.</p>
            </div>
            <div className="faq-item">
              <h3>What's included in the 14-day trial?</h3>
              <p>Full access to your chosen protection level. We do require payment details upfront to ensure serious clinics only, but you can cancel anytime before day 14.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <h2>Start sleeping better tonight</h2>
          <p className="cta-subheader">
            Your 14-day protection begins the moment you sign up
          </p>
          <button className="final-cta-button" onClick={() => scrollToSection('pricing')}>
            Protect My Practice Now
          </button>
          <div className="trust-points">
            <span>✓ 14-day free trial</span>
            <span>✓ Cancel anytime</span>
            <span>✓ Setup in minutes</span>
            <span>✓ No IT skills needed</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>MedInsight Pro</h3>
              <p>Peace of mind for Pabau clinics</p>
            </div>
            <div className="footer-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="mailto:support@medinsightpro.com">Contact Support</a>
              <a href="/login">Sign In</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 MedInsight Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
